// The main grandstand along the −z side: layered timber terraces under a sloped roof, front
// railings, hanging fabric banners, festoon lights, planters and pennants. It is built as one
// 24 m module and instanced along the whole course, so 600 m of stand costs a handful of draws.
// Painted crowd cards fill the modules that the 3D crowd does not cover.
import {
  AdditiveBlending, Color, DoubleSide, Group, IcosahedronGeometry, InstancedMesh, Matrix4, MeshBasicMaterial, MeshStandardMaterial, PlaneGeometry, Quaternion,
  Vector3, type BufferGeometry, type Material,
} from 'three';
import { STAND, rowSeat, standBack, standModulesX, rng } from './layout';
import type { EnvMaterials } from './materials';
import { BANNER_DESIGNS, bannerAtlas, crowdCard } from './signs';
import { box, merge, normalise, place } from './util';

const L = STAND.moduleLength;

/** Builds one module's geometry, split by material. Local x runs 0..L. */
function moduleGeometry(): { planks: BufferGeometry; timber: BufferGeometry; roof: BufferGeometry; cream: BufferGeometry } {
  const planks: BufferGeometry[] = [], timber: BufferGeometry[] = [], roof: BufferGeometry[] = [], cream: BufferGeometry[] = [];
  const f = STAND.frontZ, back = standBack();
  // Front wall below the first row.
  planks.push(box(L, STAND.baseY, 0.08, { at: [L / 2, STAND.baseY / 2, f], tile: 1.8, shade: 0.8 }));
  for (let r = 0; r < STAND.rows; r++) {
    const s = rowSeat(r);
    const shadeRow = 1 - r * 0.025;
    // Tread (two boards) and the riser up to the next row.
    planks.push(box(L, 0.07, STAND.rowDepth * 0.52, { at: [L / 2, s.y - 0.035, f - r * STAND.rowDepth - STAND.rowDepth * 0.26], tile: 1.8, shade: shadeRow }));
    planks.push(box(L, 0.07, STAND.rowDepth * 0.5, { at: [L / 2, s.y - 0.04, f - r * STAND.rowDepth - STAND.rowDepth * 0.75], tile: 1.8, shade: shadeRow * 0.92 }));
    planks.push(box(L, STAND.rowRise, 0.05, { at: [L / 2, s.y + STAND.rowRise / 2, f - (r + 1) * STAND.rowDepth], tile: 1.8, shade: shadeRow * 0.8 }));
  }
  // Back wall and a top rail.
  planks.push(box(L, back.y, 0.1, { at: [L / 2, back.y / 2, back.z], tile: 1.8, shade: 0.7 }));
  cream.push(box(L, 0.08, 0.1, { at: [L / 2, back.y + 0.04, back.z + 0.05], tile: 1 }));
  // Under-structure: a stringer under each column line.
  const slope = Math.atan2(STAND.rows * STAND.rowRise, STAND.rows * STAND.rowDepth);
  const sLen = Math.hypot(STAND.rows * STAND.rowRise, STAND.rows * STAND.rowDepth);
  // Columns, rafters and stringers every STAND.columnEvery metres (the module's far edge is the next module's 0).
  for (let x = 0; x < L; x += STAND.columnEvery) {
    timber.push(box(0.22, STAND.roofFrontY, 0.22, { at: [x, STAND.roofFrontY / 2, f + 0.35], tile: 1.4, grain: 'y' }));
    timber.push(box(0.22, STAND.roofBackY, 0.22, { at: [x, STAND.roofBackY / 2, back.z + 0.2], tile: 1.4, grain: 'y' }));
    const rz0 = f + 1.3, rz1 = back.z - 0.3;
    const rLen = Math.hypot(rz0 - rz1, STAND.roofBackY - STAND.roofFrontY);
    const rAng = Math.atan2(STAND.roofBackY - STAND.roofFrontY, rz0 - rz1);
    timber.push(box(0.16, 0.34, rLen, { at: [x, (STAND.roofFrontY + STAND.roofBackY) / 2 - 0.2, (rz0 + rz1) / 2], rot: [rAng, 0, 0], tile: 1.4 }));
    timber.push(box(0.16, 0.3, sLen, { at: [x, STAND.baseY / 2 + (STAND.rows * STAND.rowRise) / 2 - 0.3, f - (STAND.rows * STAND.rowDepth) / 2], rot: [slope, 0, 0], tile: 1.4, shade: 0.6 }));
    // Knee braces at the front column.
    timber.push(box(0.1, 0.1, 1.6, { at: [x, STAND.roofFrontY - 0.7, f + 0.85], rot: [-0.75, 0, 0], tile: 1 }));
  }
  // Roof boards, ridge and fascia.
  const rz0 = f + 1.4, rz1 = back.z - 0.4;
  const rLen = Math.hypot(rz0 - rz1, STAND.roofBackY - STAND.roofFrontY);
  const rAng = Math.atan2(STAND.roofBackY - STAND.roofFrontY, rz0 - rz1);
  roof.push(box(L, 0.1, rLen, { at: [L / 2, (STAND.roofFrontY + STAND.roofBackY) / 2, (rz0 + rz1) / 2], rot: [rAng, 0, 0], tile: 2.2, shade: 0.9 }));
  cream.push(box(L, 0.5, 0.08, { at: [L / 2, STAND.roofFrontY - 0.05, rz0 + 0.02], tile: 1.2 }));
  // Front railing: posts and two rails.
  for (let x = 0.5; x < L; x += 2) cream.push(box(0.06, 0.9, 0.06, { at: [x, STAND.baseY + 0.45, f + 0.02], tile: 0.6, grain: 'y' }));
  cream.push(box(L, 0.07, 0.09, { at: [L / 2, STAND.baseY + 0.92, f + 0.02], tile: 1 }));
  cream.push(box(L, 0.05, 0.05, { at: [L / 2, STAND.baseY + 0.5, f + 0.02], tile: 1 }));
  // Stairs for an aisle at the module edge: a cream stripe on each riser front.
  for (let r = 0; r < STAND.rows; r++) {
    const s = rowSeat(r);
    cream.push(box(0.9, 0.04, 0.05, { at: [1.2, s.y + STAND.rowRise * 0.5, f - (r + 1) * STAND.rowDepth + 0.03], tile: 1 }));
  }
  return { planks: merge(planks), timber: merge(timber), roof: merge(roof), cream: merge(cream) };
}

/** Painted crowd card strips, one per row, for a whole module. */
function crowdCardGeometry(): BufferGeometry {
  const parts: BufferGeometry[] = [];
  for (let r = 0; r < STAND.rows; r++) {
    const s = rowSeat(r);
    const g = new PlaneGeometry(L, 0.9);
    const uv = g.getAttribute('uv');
    // Each row uses its own band of the card (rows top to bottom = back to front).
    const v0 = 1 - (r + 1) / STAND.rows, v1 = 1 - r / STAND.rows;
    for (let k = 0; k < uv.count; k++) uv.setXY(k, uv.getX(k) * 1 + (r * 0.37) % 1, v0 + uv.getY(k) * (v1 - v0));
    parts.push(normalise(place(g, [L / 2, s.y + 0.42, s.z])));
  }
  return merge(parts);
}

export class Grandstand {
  readonly group = new Group();
  readonly modulesX = standModulesX();
  private cards: InstancedMesh;
  private hiddenFrom = -1;
  private hiddenTo = -1;
  private bulbs: InstancedMesh;
  private halos: InstancedMesh;
  private flowers: InstancedMesh[] = [];
  private bannerMats: Material[] = [];

  constructor(mats: EnvMaterials) {
    this.group.name = 'grandstand';
    const geo = moduleGeometry();
    const n = this.modulesX.length;
    const m = new Matrix4(), q = new Quaternion(), s = new Vector3(1, 1, 1), p = new Vector3();
    // The stand receives shadows but casts none. It stands south of the course, so with the real
    // sun behind it a low sun (all winter long, and mornings and evenings) would put the whole
    // track and the cats in its shade; the broadcast keeps the course in the light instead.
    const add = (g: BufferGeometry, mat: Material): InstancedMesh => {
      const im = new InstancedMesh(g, mat, n);
      this.modulesX.forEach((x, i) => im.setMatrixAt(i, m.compose(p.set(x, 0, 0), q, s)));
      im.castShadow = false; im.receiveShadow = true;
      im.frustumCulled = false;
      this.group.add(im);
      return im;
    };
    add(geo.planks, mats.planks);
    add(geo.timber, mats.timber);
    add(geo.roof, mats.roof);
    add(geo.cream, mats.paintedCream);

    // Painted crowd cards.
    const cardMat = new MeshStandardMaterial({ map: crowdCard(), alphaTest: 0.5, roughness: 0.9, side: DoubleSide });
    this.cards = add(crowdCardGeometry(), cardMat);

    // Hanging banners: two per module between the columns, alternating designs.
    const atlas = bannerAtlas();
    const bannerMat = new MeshStandardMaterial({ map: atlas, alphaTest: 0.5, roughness: 0.85, side: DoubleSide });
    bannerMat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = { value: 0 };
      bannerMat.userData.shader = shader;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nuniform float uTime;')
        .replace('#include <begin_vertex>', `#include <begin_vertex>
          float hang = clamp(-position.y / 2.8 + 0.5, 0.0, 1.0);
          float wx = (instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).x;
          transformed.z += sin(uTime * 1.3 + wx * 0.37 + position.y * 1.2) * 0.07 * hang * hang;`);
    };
    this.bannerMats.push(bannerMat);
    const bw = 1.3, bh = 2.8;
    const bannerGeos = Array.from({ length: BANNER_DESIGNS }, (_, d) => {
      const g = new PlaneGeometry(bw, bh, 1, 6);
      const uv = g.getAttribute('uv');
      for (let k = 0; k < uv.count; k++) uv.setX(k, (d + uv.getX(k)) / BANNER_DESIGNS);
      return g;
    });
    const per = bannerGeos.map((g) => new InstancedMesh(g, bannerMat, n * 2));
    const used = new Array(BANNER_DESIGNS).fill(0) as number[];
    const bz = STAND.frontZ + 1.32, by = STAND.roofFrontY - 0.3 - bh / 2;
    this.modulesX.forEach((x, i) => {
      for (const [k, off] of [[0, 9], [1, 21]] as const) {
        const d = (i * 2 + k) % BANNER_DESIGNS;
        per[d]!.setMatrixAt(used[d]!++, m.compose(p.set(x + off, by, bz), q, s));
      }
    });
    per.forEach((im, d) => { im.count = used[d]!; im.receiveShadow = true; im.frustumCulled = false; this.group.add(im); });

    // Festoon lights along the roof front: warm bulbs on a sagging wire between columns.
    const bulbsPerSpan = 9, spans = Math.round(L / STAND.columnEvery);
    const bulbMat = new MeshBasicMaterial({ color: new Color(4.5, 4.5, 4.5), toneMapped: true });
    this.bulbs = new InstancedMesh(new IcosahedronGeometry(0.075, 0), bulbMat, n * spans * bulbsPerSpan);
    const wireMat = new MeshBasicMaterial({ color: '#2a1d16' });
    const wireParts: BufferGeometry[] = [];
    let b = 0;
    const wz = STAND.frontZ + 1.25, wy = STAND.roofFrontY - 0.45;
    for (const x0 of this.modulesX) for (let sp = 0; sp < spans; sp++) {
      const xa = x0 + sp * STAND.columnEvery;
      for (let k = 0; k < bulbsPerSpan; k++) {
        const t = (k + 0.5) / bulbsPerSpan;
        const y = wy - Math.sin(t * Math.PI) * 0.75;
        this.bulbs.setMatrixAt(b++, m.compose(p.set(xa + t * STAND.columnEvery, y - 0.1, wz), q, s));
      }
    }
    for (let sp = 0; sp < spans; sp++) for (let k = 0; k < 12; k++) {
      const t0 = k / 12, t1 = (k + 1) / 12;
      const xa = sp * STAND.columnEvery;
      const y0 = wy - Math.sin(t0 * Math.PI) * 0.75, y1 = wy - Math.sin(t1 * Math.PI) * 0.75;
      const len = Math.hypot(STAND.columnEvery / 12, y1 - y0);
      wireParts.push(box(len, 0.012, 0.012, { at: [xa + (t0 + t1) / 2 * STAND.columnEvery, (y0 + y1) / 2, wz], rot: [0, 0, Math.atan2(y1 - y0, STAND.columnEvery / 12)] }));
    }
    this.bulbs.frustumCulled = false;
    this.group.add(this.bulbs);
    add(merge(wireParts), wireMat);
    // A soft additive halo around each bulb so they glow at dusk without relying only on bloom.
    const haloMat = new MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.18, blending: AdditiveBlending, depthWrite: false });
    const halos = new InstancedMesh(new IcosahedronGeometry(0.22, 0), haloMat, this.bulbs.count);
    for (let i = 0; i < this.bulbs.count; i++) { this.bulbs.getMatrixAt(i, m); halos.setMatrixAt(i, m); }
    halos.frustumCulled = false;
    this.halos = halos;
    this.group.add(halos);
    this.setBulbColours(null);

    // Planters along the walkway in front of the stand, with flowers off the running surface.
    const planterX: number[] = [];
    for (const x0 of this.modulesX) for (let k = 0; k < 4; k++) planterX.push(x0 + 3 + k * 6);
    const planterGeo = merge([
      box(1.5, 0.42, 0.5, { at: [0, 0.21, 0], tile: 1.2, shade: 0.9 }),
      box(1.4, 0.04, 0.42, { at: [0, 0.4, 0], tile: 1.2, shade: [0.35, 0.25, 0.18] }),
    ]);
    const planters = new InstancedMesh(planterGeo, mats.planks, planterX.length);
    planterX.forEach((x, i) => planters.setMatrixAt(i, m.compose(p.set(x, 0, STAND.frontZ + 0.75), q, s)));
    planters.castShadow = true; planters.receiveShadow = true;
    this.group.add(planters);
    this.addFlowers(planterX, STAND.frontZ + 0.75);
  }

  private addFlowers(xs: number[], z: number): void {
    const r = rng(77);
    const per = 26;
    const leaves = new InstancedMesh(new IcosahedronGeometry(0.11, 0), new MeshStandardMaterial({ color: '#3d6b2a', roughness: 0.8 }), xs.length * per);
    const blooms = new InstancedMesh(new IcosahedronGeometry(0.06, 0), new MeshStandardMaterial({ color: '#ffffff', roughness: 0.6 }), xs.length * per);
    const colours = ['#ff5fa8', '#ffffff', '#ffd23f', '#b46cff', '#ff7a5c', '#ffeef6'].map((c) => new Color(c));
    const m = new Matrix4(), q = new Quaternion(), s = new Vector3(), p = new Vector3();
    let i = 0;
    for (const x of xs) for (let k = 0; k < per; k++) {
      const px = x + (r() - 0.5) * 1.35, pz = z + (r() - 0.5) * 0.36;
      leaves.setMatrixAt(i, m.compose(p.set(px, 0.46 + r() * 0.1, pz), q.random(), s.setScalar(0.8 + r() * 0.7)));
      blooms.setMatrixAt(i, m.compose(p.set(px + (r() - 0.5) * 0.1, 0.58 + r() * 0.16, pz), q.random(), s.setScalar(0.8 + r() * 0.6)));
      blooms.setColorAt(i, colours[Math.floor(r() * colours.length)]!);
      i++;
    }
    for (const im of [leaves, blooms]) { im.castShadow = true; im.receiveShadow = true; im.frustumCulled = false; this.group.add(im); }
    this.flowers.push(leaves, blooms);
    blooms.instanceColor!.needsUpdate = true;
  }

  /** Festoon bulb colours, cycled along the wire; null restores the warm white bulbs. */
  setBulbColours(colours: string[] | null): void {
    const list = colours ? colours.map((c) => new Color(c)) : [new Color('#ffcf8a')];
    const halo = colours ? null : new Color('#ffb066');
    const c = new Color();
    for (let i = 0; i < this.bulbs.count; i++) {
      c.copy(list[i % list.length]!);
      this.bulbs.setColorAt(i, c);
      this.halos.setColorAt(i, halo ?? c);
    }
    this.bulbs.instanceColor!.needsUpdate = true;
    this.halos.instanceColor!.needsUpdate = true;
  }

  /** Shows or hides the flowers in the planters (they are out from late spring to early autumn). */
  setFlowers(on: boolean): void {
    for (const f of this.flowers) f.visible = on;
  }

  /** Hides the painted crowd cards on modules [from, to) where the 3D crowd sits. */
  setLiveCrowdModules(from: number, to: number): void {
    if (from === this.hiddenFrom && to === this.hiddenTo) return;
    this.hiddenFrom = from; this.hiddenTo = to;
    const m = new Matrix4(), q = new Quaternion(), p = new Vector3();
    this.modulesX.forEach((x, i) => {
      const hide = i >= from && i < to;
      this.cards.setMatrixAt(i, m.compose(p.set(x, hide ? -50 : 0, 0), q, new Vector3(1, hide ? 0 : 1, 1)));
    });
    this.cards.instanceMatrix.needsUpdate = true;
  }

  update(timeS: number): void {
    for (const mat of this.bannerMats) {
      const sh = mat.userData.shader as { uniforms: { uTime: { value: number } } } | undefined;
      if (sh) sh.uniforms.uTime.value = timeS;
    }
  }
}
